import { useForm } from '@nerdgeschoss/react-use-form-library';
import React, { useContext } from 'react';

import { ActivitiesContext } from '../../../../components/Activities/Activities';
import { FormForm } from '../../../../components/Form/FormForm';
import { Switcher } from '../../../../components/Form/Switcher/Switcher';
import { FormTextField } from '../../../../components/Form/TextField/FormTextField';
import { useModal } from '../../../../components/Modal/PresentedModal';
import { Stack } from '../../../../components/Stack/Stack';
import { graphql } from '../../../../generated';
import { CustomEmailSender } from '../../../../generated/graphql';
import { t } from '../../../../locales/translate';
import { useMutationHandler } from '../../../../model/graphql';
import { FormFileUpload } from '../../../../components/Form/FileUpload/FormFileUpload';
import { FormTextArea } from '../../../../components/Form/TextArea/FormTextArea';

const SAVE_CUSTOM_EMAIL = graphql(`
  mutation SaveCustomEmail(
    $id: ID!
    $email: String
    $displayName: String
    $active: Boolean!
    $bodyBackgroundColor: String
    $bodyForegroundColor: String
    $buttonBackgroundColor: String
    $buttonForegroundColor: String
    $footerBackgroundColor: String
    $footerForegroundColor: String
    $emailBackgroundColor: String
    $separatorLineColor: String
    $closingNotes: String
    $headerImage: Upload
  ) {
    updateCustomEmailSender(
      input: {
        id: $id
        email: $email
        displayName: $displayName
        active: $active
        bodyBackgroundColor: $bodyBackgroundColor
        bodyForegroundColor: $bodyForegroundColor
        buttonBackgroundColor: $buttonBackgroundColor
        buttonForegroundColor: $buttonForegroundColor
        footerBackgroundColor: $footerBackgroundColor
        footerForegroundColor: $footerForegroundColor
        emailBackgroundColor: $emailBackgroundColor
        separatorLineColor: $separatorLineColor
        closingNotes: $closingNotes
        headerImage: $headerImage
      }
    ) {
      shop {
        id
        customEmailSender {
          id
          active
          displayName
          email

          headerImage {
            fileType
            id
            ... on Image {
              url(width: 200)
            }
          }

          bodyBackgroundColor
          bodyForegroundColor
          buttonBackgroundColor
          buttonForegroundColor
          footerBackgroundColor
          footerForegroundColor
          emailBackgroundColor
          separatorLineColor
          closingNotes
        }
      }
    }
  }
`);

type Props = {
  customEmailSender: Omit<CustomEmailSender, 'headerImage'> & { headerImage: { fileType: string } | null };
  shopId: string;
};

export function CustomEmailSenderForm({ customEmailSender, shopId }: Props): JSX.Element {
  const { Modal, open, close } = useModal();
  const [saveCustomEmail] = useMutationHandler(SAVE_CUSTOM_EMAIL);
  const { reloadActivities } = useContext(ActivitiesContext);

  const headerImage = customEmailSender.headerImage ? new File([], customEmailSender.headerImage.fileType) : undefined;

  const { fields, valid, onSubmit, submissionStatus } = useForm({
    model: {
      ...customEmailSender,
      headerImage,
    },
    validations: {
      email: 'email',
      active: 'required',
    },
    onSubmit: async ({ model }) => {
      let headerImage: File | null | undefined = model.headerImage;
      if (headerImage?.size === 0) {
        // If the headerImage is empty, this is only the placeholder for the existing file
        // and we don't want to update it.
        headerImage = undefined;
      }

      await saveCustomEmail({
        variables: {
          ...model,
          id: shopId,
          active: Boolean(model.active),
          headerImage,
        },
      });
      reloadActivities();
      close();
    },
  });

  return (
    <Modal open={open} title={t.model.shop.customEmailSender}>
      <FormForm onClose={close} onSubmit={onSubmit} canSubmit={valid} loading={submissionStatus === 'submitting'}>
        <Stack>
          <Switcher {...fields.active} labels={[t.general.yes, t.general.no]} />

          {fields.active.value && (
            <>
              <FormTextField
                name="displayName"
                {...fields.displayName}
                label={t.model.shop.customEmailSenderFields.displayName}
              />
              <FormTextField name="email" {...fields.email} label={t.model.shop.customEmailSenderFields.email} />

              <FormFileUpload
                name="headerImage"
                {...fields.headerImage}
                label={t.model.shop.customEmailSenderFields.headerImage}
                accept="image/*"
              />

              <FormTextField
                name="bodyBackgroundColor"
                {...fields.bodyBackgroundColor}
                label={t.model.shop.customEmailSenderFields.bodyBackgroundColor}
              />
              <FormTextField
                name="bodyForegroundColor"
                {...fields.bodyForegroundColor}
                label={t.model.shop.customEmailSenderFields.bodyForegroundColor}
              />
              <FormTextField
                name="buttonBackgroundColor"
                {...fields.buttonBackgroundColor}
                label={t.model.shop.customEmailSenderFields.buttonBackgroundColor}
              />
              <FormTextField
                name="buttonForegroundColor"
                {...fields.buttonForegroundColor}
                label={t.model.shop.customEmailSenderFields.buttonForegroundColor}
              />
              <FormTextField
                name="footerBackgroundColor"
                {...fields.footerBackgroundColor}
                label={t.model.shop.customEmailSenderFields.footerBackgroundColor}
              />
              <FormTextField
                name="footerForegroundColor"
                {...fields.footerForegroundColor}
                label={t.model.shop.customEmailSenderFields.footerForegroundColor}
              />
              <FormTextField
                name="emailBackgroundColor"
                {...fields.emailBackgroundColor}
                label={t.model.shop.customEmailSenderFields.emailBackgroundColor}
              />
              <FormTextField
                name="separatorLineColor"
                {...fields.separatorLineColor}
                label={t.model.shop.customEmailSenderFields.separatorLineColor}
              />
              <FormTextArea
                name="closingNotes"
                {...fields.closingNotes}
                label={t.model.shop.customEmailSenderFields.closingNotes}
              />
            </>
          )}
        </Stack>
      </FormForm>
    </Modal>
  );
}
